import { useTranslation } from 'react-i18next';
import DOCUMENT_VALIDATION from 'constants/documentValidation';

/**
 * useBillingOperations Hook
 *
 * Custom hook to handle billing operations logic and validation.
 * Focused on billing-specific operations and validations.
 *
 * @param {string} purchaseToken - Token used for purchase operations
 * @param {string} rucNumber - The RUC number from useRucValidation
 * @param {object} taxpayerData - The taxpayer data from useRucValidation
 * @param {Function} setErrorMessage - Function to set error messages
 * @returns {object} Object containing billing operation functions
 */
const useBillingOperations = (purchaseToken, rucNumber, taxpayerData, setErrorMessage) => {
  const { t } = useTranslation('exchange');

  /**
   * validateBillingInputs Function
   *
   * Validates the billing inputs for the billing operation.
   *
   * @param {object} selectedTicket - The selected ticket with document information
   * @returns {boolean} True if the billing inputs are valid, false otherwise
   */
  const validateBillingInputs = (selectedTicket) => {
    if (!rucNumber.trim()) {
      setErrorMessage(t('modal_confirm_billing.ruc_required'));
      return false;
    }

    if (!DOCUMENT_VALIDATION.PERU.RUC.PATTERN.test(rucNumber.trim())) {
      setErrorMessage(t('modal_confirm_billing.invalid_ruc'));
      return false;
    }

    if (!taxpayerData) {
      setErrorMessage(t('modal_confirm_billing.invalid_ruc'));
      return false;
    }

    if (!selectedTicket) {
      setErrorMessage(t('modal_confirm_billing.no_ticket_selected'));
      return false;
    }

    const { companyCode, documentType, documentNumber } = selectedTicket;

    if (!companyCode || !documentType || !documentNumber) {
      setErrorMessage(t('modal_confirm_billing.missing_ticket_data'));
      return false;
    }

    return true;
  };

  /**
   * prepareBillingPayload Function
   *
   * Prepares the billing payload for the billing operation.
   *
   * @param {object} selectedTicket - The selected ticket with document information
   * @returns {object} The prepared billing payload
   */
  const prepareBillingPayload = (selectedTicket) => {
    const { companyCode, documentType, documentNumber, documentPrefix } = selectedTicket;

    return {
      companyCode,
      documentType,
      documentNumber,
      documentPrefix: documentPrefix || '',
      taxPayerId: rucNumber.trim(),
      token: purchaseToken,
    };
  };

  return {
    validateBillingInputs,
    prepareBillingPayload,
  };
};

export default useBillingOperations;
